import { Analytics } from '@cobliteam/analytics';
import type { Location } from '@cobliteam/common-types';
import { useUser } from '@cobliteam/hooks';
import type { MaturityItemId } from '@cobliteam/hooks/src/useBasicValueMaturity/basicValueMaturityModel';
import useBasicValueMaturity from '@cobliteam/hooks/src/useBasicValueMaturity/useBasicValueMaturity';
import {
  isBasicValueDismissed,
  isBasicValueSnoozed,
  persistBasicValueDismissed,
  persistBasicValueSnooze,
} from '@cobliteam/ui-business/components/dialogs/DialogBasicValueOnboarding/basicValueDismiss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface BasicValuePendingFeature {
  id: MaturityItemId;
  /** Destination route for the "go set it up" CTA. */
  route: Location;
}

/**
 * Source of truth for the guided onboarding modal: which Basic Value items are still pending.
 *
 * Items + done-status come from {@link useBasicValueMaturity} (the same model the Basic Value page
 * uses), so the modal lists ALL maturity items and hides the ones already at the minimum. This hook
 * adds the show/dismiss gating and the "Shown" analytics on top of that shared model.
 */
interface UseBasicValueOnboardingSourceArgs {
  /** Force the maturity queries to run even when the auto-popup is gated (e.g. opened from the page). */
  forceActive?: boolean;
}

const useBasicValueOnboardingSource = ({ forceActive = false }: UseBasicValueOnboardingSourceArgs = {}) => {
  const { session, noValidGroupOrVehicles } = useUser();

  const fleetId = session?.fleet.id;
  const [dismissed, setDismissed] = useState(() => (fleetId ? isBasicValueDismissed(fleetId) : false));

  // "Não quero mais ver isso" snoozes the auto-popup for 4 days (localStorage).
  const snoozed = fleetId ? isBasicValueSnoozed(fleetId) : false;
  const enabled = Boolean(fleetId && !dismissed && !snoozed && !noValidGroupOrVehicles);

  const maturity = useBasicValueMaturity({ enabled: enabled || forceActive });

  const isLoading = maturity.isLoading;
  const isError = maturity.isError;

  // Pending items the user can actually act on (not done + has permission), in level order.
  const pendingFeatures = useMemo<BasicValuePendingFeature[]>(
    () => maturity.items.filter((item) => !item.done && item.allowed).map((item) => ({ id: item.id, route: item.route })),
    [maturity.items]
  );

  const onClose = useCallback(() => {
    if (!fleetId) return;
    persistBasicValueDismissed(fleetId);
    setDismissed(true);
  }, [fleetId]);

  // Persist the guide as dismissed WITHOUT closing the dialog — used after the user
  // completes an inline action (e.g. registers a driver) so the guide won't reappear
  // on the next load, while the current dialog stays open for follow-up steps.
  const markDismissed = useCallback(() => {
    if (!fleetId) return;
    persistBasicValueDismissed(fleetId);
  }, [fleetId]);

  // "Não quero mais ver isso": snooze the auto-popup for 4 days and close it now.
  const snooze = useCallback(() => {
    if (!fleetId) return;
    persistBasicValueSnooze(fleetId);
    setDismissed(true);
  }, [fleetId]);

  // The modal is now manual-only (opened from the home hero or the Basic Value page); there is no
  // auto-popup. Track "Shown" once per fleet when the manually-opened dialog actually becomes visible.
  const isVisible = forceActive && !isLoading && !isError && pendingFeatures.length > 0;
  const shownForFleetRef = useRef<string | null>(null);
  useEffect(() => {
    if (!(isVisible && fleetId) || shownForFleetRef.current === fleetId) return;
    shownForFleetRef.current = fleetId;
    Analytics.track('BasicValueModalShown', {
      fleetId,
      pendingFeatures: pendingFeatures.map((feature) => feature.id),
      totalSteps: pendingFeatures.length,
    });
  }, [isVisible, fleetId, pendingFeatures]);

  return {
    isLoading,
    fleetId,
    pendingFeatures,
    areas: maturity.areas,
    refetch: maturity.refetch,
    onClose,
    markDismissed,
    snooze,
  };
};

export default useBasicValueOnboardingSource;
